import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/endpoints.js';
import { setToken, getToken, onUnauthorized } from '../api/client.js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | authenticated | anonymous

  const applyUser = useCallback(
    (nextUser) => {
      setUser(nextUser);
      setStatus(nextUser ? 'authenticated' : 'anonymous');
      // The user's saved language wins over the browser detection.
      const locale = nextUser?.preferences?.locale;
      if (locale && locale !== i18n.resolvedLanguage) i18n.changeLanguage(locale);
    },
    [i18n],
  );

  const signOut = useCallback(() => {
    setToken(null);
    applyUser(null);
  }, [applyUser]);

  // Restore the session on a page reload.
  useEffect(() => {
    if (!getToken()) {
      setStatus('anonymous');
      return;
    }
    authApi
      .me()
      .then(applyUser)
      .catch(() => signOut());
  }, [applyUser, signOut]);

  // Any 401 anywhere in the app drops the session.
  useEffect(() => onUnauthorized(signOut), [signOut]);

  const signIn = useCallback(
    async (email, password) => {
      const result = await authApi.login(email, password);
      setToken(result.token);
      applyUser(result.user);
      return result.user;
    },
    [applyUser],
  );

  const setLocale = useCallback(
    async (locale) => {
      i18n.changeLanguage(locale);
      if (user) {
        const updated = await authApi.updatePreferences({ locale }).catch(() => null);
        if (updated) setUser((current) => ({ ...current, preferences: updated.preferences }));
      }
    },
    [i18n, user],
  );

  const value = useMemo(
    () => ({
      user,
      status,
      isAuthenticated: status === 'authenticated',
      signIn,
      signOut,
      setLocale,
      hasPermission: (permission) => Boolean(user?.permissions?.includes(permission)),
      hasRole: (...roles) => Boolean(user && roles.includes(user.role)),
    }),
    [user, status, signIn, signOut, setLocale],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
};
